import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import './Profiles.css';

const PlateDeliveries = () => {
  const { user } = useContext(AuthContext);
  
  // Estados principales
  const [activeTab, setActiveTab] = useState('deliveries'); // deliveries, routes, daily-summary
  const [deliveries, setDeliveries] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [dailySummary, setDailySummary] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Fecha actual
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Paginación y Filtros
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');

  // Modales
  const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [evidenceType, setEvidenceType] = useState('placa'); // 'placa' o 'tarjeta'
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidencePreview, setEvidencePreview] = useState('');
  const [uploading, setUploading] = useState(false);

  const [showIncidenceModal, setShowIncidenceModal] = useState(false);
  const [incidenceReason, setIncidenceReason] = useState('');
  const [submittingIncidence, setSubmittingIncidence] = useState(false);

  const [showContactModal, setShowContactModal] = useState(false);
  const [contactData, setContactData] = useState({ direccion: '', contacto: '', telefono: '' });

  const [showRouteModal, setShowRouteModal] = useState(false);
  const [newRouteData, setNewRouteData] = useState({ fecha_ruta: '', repartidor_id: '', transito_id: '', observaciones: '' });
  
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedDeliveries, setSelectedDeliveries] = useState([]);

  // Fetch data functions
  const fetchDeliveries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/plate-deliveries');
      setDeliveries(res.data);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Error al cargar las entregas de especies venales');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRoutes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/plate-deliveries/routes');
      setRoutes(res.data);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Error al cargar las rutas de entrega');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDailySummary = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/plate-deliveries/daily-summary', {
        params: { fecha: selectedDate }
      });
      setDailySummary(res.data);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Error al cargar el resumen diario');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (activeTab === 'deliveries') {
      fetchDeliveries();
    } else if (activeTab === 'routes') {
      fetchRoutes();
    } else if (activeTab === 'daily-summary') {
      fetchDailySummary();
    }
  }, [activeTab, fetchDeliveries, fetchRoutes, fetchDailySummary]);

  // Actualizar estado de entrega
  const handleUpdateStatus = async (id, newStatus) => {
    const actionWords = {
      'recolectado': 'marcar como recogida',
      'confirmado': 'confirmar la recepción'
    };
    const actionWord = actionWords[newStatus] || newStatus;
    if (!window.confirm(`¿Confirmar ${actionWord.toUpperCase()} de esta especie venal?`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      const res = await axios.put(`/api/plate-deliveries/${id}/status`, { status: newStatus });
      setSuccess(res.data.message || 'Estado actualizado con éxito');
      fetchDeliveries();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al actualizar el estado');
    }
  };

  // Abrir Modal de Evidencia
  const openEvidenceModal = (id, type = 'placa') => {
    setSelectedDeliveryId(id);
    setEvidenceType(type);
    setEvidenceFile(null);
    setEvidencePreview('');
    setShowEvidenceModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEvidenceFile(file);
      setEvidencePreview(URL.createObjectURL(file));
    }
  };

  const handleUploadEvidence = async (e) => {
    e.preventDefault();
    if (!evidenceFile) {
      alert('Debe cargar una foto como evidencia.');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('evidence', evidenceFile);

      let endpoint = '';
      if (evidenceType === 'placa') {
        formData.append('status', 'entregado');
        endpoint = `/api/plate-deliveries/${selectedDeliveryId}/status`;
      } else {
        endpoint = `/api/plate-deliveries/${selectedDeliveryId}/tarjeta`;
      }

      const res = await axios.put(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(res.data.message || `${evidenceType === 'placa' ? 'Placa' : 'Tarjeta de propiedad'} marcada como entregada con evidencia.`);
      setShowEvidenceModal(false);
      fetchDeliveries();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al subir la evidencia de entrega.');
    } finally {
      setUploading(false);
    }
  };

  // Modal de Incidencia
  const openIncidenceModal = (id) => {
    setSelectedDeliveryId(id);
    setIncidenceReason('');
    setShowIncidenceModal(true);
  };

  const handleReportIncidence = async (e) => {
    e.preventDefault();
    if (!incidenceReason.trim()) {
      alert('Por favor ingrese el motivo del reclamo.');
      return;
    }

    try {
      setSubmittingIncidence(true);
      setError('');
      setSuccess('');

      const res = await axios.put(`/api/plate-deliveries/${selectedDeliveryId}/status`, {
        status: 'incidencia',
        observaciones: incidenceReason.trim()
      });

      setSuccess(res.data.message || 'Incidencia reportada correctamente.');
      setShowIncidenceModal(false);
      fetchDeliveries();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al registrar la incidencia.');
    } finally {
      setSubmittingIncidence(false);
    }
  };

  // Modal de Contacto
  const openContactModal = (delivery) => {
    setSelectedDeliveryId(delivery.id);
    setContactData({
      direccion: delivery.direccion_entrega || '',
      contacto: delivery.contacto_entrega || '',
      telefono: delivery.telefono_entrega || ''
    });
    setShowContactModal(true);
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      const res = await axios.put(`/api/plate-deliveries/${selectedDeliveryId}/contact`, contactData);
      setSuccess(res.data.message || 'Datos de contacto actualizados');
      setShowContactModal(false);
      fetchDeliveries();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al actualizar datos de contacto');
    }
  };

  // Crear nueva ruta
  const openRouteModal = () => {
    setNewRouteData({ 
      fecha_ruta: new Date().toISOString().split('T')[0], 
      repartidor_id: user?.id || '', 
      transito_id: '', 
      observaciones: '' 
    });
    setShowRouteModal(true);
  };

  const handleCreateRoute = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      const res = await axios.post('/api/plate-deliveries/routes', newRouteData);
      setSuccess(res.data.message || 'Ruta creada exitosamente');
      setShowRouteModal(false);
      fetchRoutes();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear la ruta');
    }
  };

  // Asignar entregas a ruta
  const openAssignModal = (route) => {
    setSelectedRoute(route);
    setSelectedDeliveries([]);
    setShowAssignModal(true);
  };

  const handleAssignDeliveries = async () => {
    if (selectedDeliveries.length === 0) {
      alert('Debe seleccionar al menos una entrega');
      return;
    }

    try {
      setError('');
      setSuccess('');
      const res = await axios.post(`/api/plate-deliveries/routes/${selectedRoute.id}/assign`, {
        delivery_ids: selectedDeliveries
      });
      setSuccess(res.data.message || 'Entregas asignadas a la ruta');
      setShowAssignModal(false);
      fetchRoutes();
      fetchDeliveries();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al asignar entregas');
    }
  };

  // Actualizar estado de ruta
  const handleUpdateRouteStatus = async (routeId, status) => {
    try {
      setError('');
      setSuccess('');
      const res = await axios.put(`/api/plate-deliveries/routes/${routeId}/status`, { status });
      setSuccess(res.data.message || 'Estado de ruta actualizado');
      fetchRoutes();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al actualizar estado de ruta');
    }
  };

  // Filtrado y búsqueda
  const filteredDeliveries = deliveries.filter(d => {
    const matchesStatus = filterStatus === 'todos' || d.status === filterStatus;
    const matchesSearch = 
      d.plate_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.concesionario_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.transito_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `#${d.tramite_id}`.includes(searchTerm);
    return matchesStatus && matchesSearch;
  });

  // Paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredDeliveries.slice(indexOfFirstItem, indexOfLastItem);

  const getStatusBadge = (status) => {
    const statuses = {
      'pendiente_recolectar': { label: '🏢 En Tránsito (Por Recoger)', class: 'pending' },
      'recolectado': { label: '🚚 En Camino (Ruta)', class: 'warning' },
      'entregado': { label: '🏪 Entregada (Por Confirmar)', class: 'warning' },
      'confirmado': { label: '✅ Recibida y Confirmada', class: 'paid' },
      'incidencia': { label: '⚠️ Incidencia (No Recibida)', class: 'expired' }
    };
    const s = statuses[status] || { label: status, class: '' };
    return <span className={`status ${s.class}`}>{s.label}</span>;
  };

  // Métricas rápidas
  const countPending = deliveries.filter(d => d.status === 'pendiente_recolectar').length;
  const countInTransit = deliveries.filter(d => d.status === 'recolectado').length;
  const countDelivered = deliveries.filter(d => d.status === 'confirmado').length;

  // Verificación de roles
  const canDriverAct = (d) => {
    if (user?.role === 'superadmin') return true;
    if (!user?.concesionario_id && !user?.transito_id) return true;
    return false;
  };

  const canRecipientAct = (d) => {
    if (user?.role === 'superadmin') return true;
    if (user?.profile_name === 'administrativo_concesionario') return false; // solo consulta
    if (user?.concesionario_id && user.concesionario_id === d.concesionario_id) return true;
    return false;
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Navbar title="📋 Especies Venales - Control de Entrega" />

        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}
          {success && <div className="success-banner">{success}</div>}

          {/* Tabs de Navegación */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #e2e8f0' }}>
            <button
              onClick={() => setActiveTab('deliveries')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: activeTab === 'deliveries' ? '#2563eb' : 'transparent',
                color: activeTab === 'deliveries' ? '#fff' : '#64748b',
                fontWeight: '600',
                cursor: 'pointer',
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.2s'
              }}
            >
              📦 Entregas
            </button>
            <button
              onClick={() => setActiveTab('routes')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: activeTab === 'routes' ? '#2563eb' : 'transparent',
                color: activeTab === 'routes' ? '#fff' : '#64748b',
                fontWeight: '600',
                cursor: 'pointer',
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.2s'
              }}
            >
              🗺️ Rutas
            </button>
            <button
              onClick={() => setActiveTab('daily-summary')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: activeTab === 'daily-summary' ? '#2563eb' : 'transparent',
                color: activeTab === 'daily-summary' ? '#fff' : '#64748b',
                fontWeight: '600',
                cursor: 'pointer',
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.2s'
              }}
            >
              📊 Resumen Diario
            </button>
          </div>

          {/* TAB: ENTREGAS */}
          {activeTab === 'deliveries' && (
            <>
              {/* Métricas */}
              <div className="metrics-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '25px' }}>
                <div className="metric-card" style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', borderLeft: '5px solid #ef4444' }}>
                  <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Pendientes en Tránsito</div>
                  <div style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginTop: '5px' }}>{countPending}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Listas para recolectar</div>
                </div>
                <div className="metric-card" style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', borderLeft: '5px solid #f59e0b' }}>
                  <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>En Ruta de Entrega</div>
                  <div style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginTop: '5px' }}>{countInTransit}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>En manos del repartidor</div>
                </div>
                <div className="metric-card" style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', borderLeft: '5px solid #10b981' }}>
                  <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Entregadas con Éxito</div>
                  <div style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginTop: '5px' }}>{countDelivered}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Confirmadas</div>
                </div>
              </div>

              {/* Filtros */}
              <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', background: '#fff', padding: '15px', borderRadius: '10px', boxShadow: '0 1px 3px rgb(0 0 0 / 0.1)', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <input 
                    type="text" 
                    placeholder="🔍 Buscar por Placa, Concesionario o Tránsito..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    onClick={() => { setFilterStatus('todos'); setCurrentPage(1); }}
                    style={{ padding: '8px 16px', borderRadius: '6px', border: filterStatus === 'todos' ? '1px solid #2563eb' : '1px solid #e2e8f0', background: filterStatus === 'todos' ? '#eff6ff' : '#fff', color: filterStatus === 'todos' ? '#2563eb' : '#64748b', fontWeight: '600', cursor: 'pointer' }}
                  >
                    Todos
                  </button>
                  <button 
                    onClick={() => { setFilterStatus('pendiente_recolectar'); setCurrentPage(1); }}
                    style={{ padding: '8px 16px', borderRadius: '6px', border: filterStatus === 'pendiente_recolectar' ? '1px solid #dc2626' : '1px solid #e2e8f0', background: filterStatus === 'pendiente_recolectar' ? '#fef2f2' : '#fff', color: filterStatus === 'pendiente_recolectar' ? '#dc2626' : '#64748b', fontWeight: '600', cursor: 'pointer' }}
                  >
                    Por Recoger
                  </button>
                  <button 
                    onClick={() => { setFilterStatus('recolectado'); setCurrentPage(1); }}
                    style={{ padding: '8px 16px', borderRadius: '6px', border: filterStatus === 'recolectado' ? '1px solid #d97706' : '1px solid #e2e8f0', background: filterStatus === 'recolectado' ? '#fffbeb' : '#fff', color: filterStatus === 'recolectado' ? '#d97706' : '#64748b', fontWeight: '600', cursor: 'pointer' }}
                  >
                    En Ruta
                  </button>
                  <button 
                    onClick={() => { setFilterStatus('entregado'); setCurrentPage(1); }}
                    style={{ padding: '8px 16px', borderRadius: '6px', border: filterStatus === 'entregado' ? '1px solid #059669' : '1px solid #e2e8f0', background: filterStatus === 'entregado' ? '#ecfdf5' : '#fff', color: filterStatus === 'entregado' ? '#059669' : '#64748b', fontWeight: '600', cursor: 'pointer' }}
                  >
                    Entregadas
                  </button>
                </div>
              </div>

              {/* Tabla */}
              {loading ? (
                <p className="loading">Cargando entregas...</p>
              ) : (
                <div className="table-wrapper">
                  <div className="table-responsive">
                    <table className="users-table">
                      <thead>
                        <tr>
                          <th>Trámite</th>
                          <th>Placa</th>
                          <th>Origen (Tránsito)</th>
                          {(!user?.transito_id && user?.role !== 'transito') && <th>Destino (Concesionario)</th>}
                          <th>Estado</th>
                          <th>Tarjeta Propiedad</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map(d => (
                          <tr key={d.id}>
                            <td>
                              <strong>#{d.tramite_id}</strong>
                              <div style={{ fontSize: '11px', color: '#64748b' }}>{d.tramite_type_name}</div>
                            </td>
                            <td>
                              <span className="plate-badge-sm" style={{ fontSize: '13px', fontWeight: 'bold' }}>{d.plate_number}</span>
                              <div style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'capitalize' }}>{d.vehicle_type}</div>
                            </td>
                            <td>
                              <strong>{d.transito_name}</strong>
                            </td>
                            {(!user?.transito_id && user?.role !== 'transito') && (
                              <td>
                                <strong>{d.concesionario_name}</strong>
                                {(d.direccion_entrega || d.concesionario_address) && (
                                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }} title={d.direccion_entrega || d.concesionario_address}>
                                    📍 {d.direccion_entrega || d.concesionario_address}
                                  </div>
                                )}
                                {(d.telefono_entrega || d.concesionario_phone) && (
                                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px' }}>
                                    📞 {d.telefono_entrega || d.concesionario_phone}
                                  </div>
                                )}
                                {canDriverAct(d) && d.status !== 'confirmado' && (
                                  <button
                                    onClick={() => openContactModal(d)}
                                    style={{ fontSize: '10px', marginTop: '4px', padding: '3px 8px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', cursor: 'pointer' }}
                                  >
                                    ✏️ Editar
                                  </button>
                                )}
                              </td>
                            )}
                            <td>{getStatusBadge(d.status)}</td>
                            <td>
                              {d.tarjeta_propiedad_entregada ? (
                                <div style={{ color: '#10b981', fontSize: '12px' }}>
                                  <strong>✅ Entregada</strong>
                                  {d.tarjeta_entregada_at && (
                                    <div style={{ fontSize: '10px', color: '#64748b' }}>
                                      {new Date(d.tarjeta_entregada_at).toLocaleString()}
                                    </div>
                                  )}
                                  {d.tarjeta_evidence_url && (
                                    <a 
                                      href={`${axios.defaults.baseURL || ''}${d.tarjeta_evidence_url}`} 
                                      target="_blank" 
                                      rel="noopener noreferrer" 
                                      style={{ color: '#2563eb', fontSize: '10px', textDecoration: 'underline' }}
                                    >
                                      📷 Ver
                                    </a>
                                  )}
                                </div>
                              ) : (
                                <div style={{ color: '#94a3b8', fontSize: '11px', fontStyle: 'italic' }}>
                                  Pendiente
                                  {canDriverAct(d) && (d.status === 'recolectado' || d.status === 'entregado') && (
                                    <button
                                      onClick={() => openEvidenceModal(d.id, 'tarjeta')}
                                      style={{ display: 'block', marginTop: '4px', fontSize: '10px', padding: '3px 8px', background: '#eff6ff', border: '1px solid #3b82f6', borderRadius: '4px', cursor: 'pointer', color: '#2563eb' }}
                                    >
                                      📄 Marcar entregada
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                            <td className="actions">
                              {/* ACCIONES DEL REPARTIDOR */}
                              {canDriverAct(d) && (
                                <>
                                  {d.status === 'pendiente_recolectar' && (
                                    <button
                                      onClick={() => handleUpdateStatus(d.id, 'recolectado')}
                                      className="btn-action btn-view"
                                      style={{ background: '#3b82f6', color: '#fff', padding: '6px 12px', borderRadius: '6px', whiteSpace: 'nowrap', border: 'none', cursor: 'pointer' }}
                                    >
                                      🚚 Recoger
                                    </button>
                                  )}
                                  {(d.status === 'recolectado' || d.status === 'incidencia') && (
                                    <button
                                      onClick={() => openEvidenceModal(d.id, 'placa')}
                                      className="btn-action btn-edit"
                                      style={{ background: '#10b981', color: '#fff', padding: '6px 12px', borderRadius: '6px', whiteSpace: 'nowrap', border: 'none', cursor: 'pointer' }}
                                    >
                                      {d.status === 'incidencia' ? '🔄 Re-entregar' : '✅ Entregar'}
                                    </button>
                                  )}
                                </>
                              )}

                              {/* ACCIONES DEL CONCESIONARIO */}
                              {canRecipientAct(d) && d.status === 'entregado' && (
                                <div style={{ display: 'flex', gap: '5px' }}>
                                  <button
                                    onClick={() => handleUpdateStatus(d.id, 'confirmado')}
                                    style={{ background: '#10b981', color: '#fff', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', border: 'none', cursor: 'pointer' }}
                                  >
                                    ✓ Confirmar
                                  </button>
                                  <button
                                    onClick={() => openIncidenceModal(d.id)}
                                    style={{ background: '#ef4444', color: '#fff', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', border: 'none', cursor: 'pointer' }}
                                  >
                                    ❌ Reclamar
                                  </button>
                                </div>
                              )}

                              {(!canDriverAct(d) && !canRecipientAct(d)) && (
                                <span style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic' }}>-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {filteredDeliveries.length === 0 && (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                      No hay entregas registradas.
                    </div>
                  )}

                  <Pagination 
                    currentPage={currentPage}
                    totalItems={filteredDeliveries.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={(page) => setCurrentPage(page)}
                    onItemsPerPageChange={(items) => { setItemsPerPage(items); setCurrentPage(1); }}
                  />
                </div>
              )}
            </>
          )}

          {/* TAB: RUTAS */}
          {activeTab === 'routes' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3>Rutas de Entrega</h3>
                <button
                  onClick={openRouteModal}
                  style={{ padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
                >
                  ➕ Crear Nueva Ruta
                </button>
              </div>

              {loading ? (
                <p className="loading">Cargando rutas...</p>
              ) : (
                <div style={{ display: 'grid', gap: '15px' }}>
                  {routes.map(route => (
                    <div key={route.id} style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 4px rgb(0 0 0 / 0.1)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                        <div>
                          <h4 style={{ margin: 0, marginBottom: '5px' }}>🗺️ Ruta #{route.id} - {new Date(route.fecha_ruta).toLocaleDateString()}</h4>
                          <div style={{ fontSize: '13px', color: '#64748b' }}>
                            <strong>Repartidor:</strong> {route.repartidor_name} | <strong>Tránsito:</strong> {route.transito_name}
                          </div>
                          {route.observaciones && (
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '5px', fontStyle: 'italic' }}>
                              📝 {route.observaciones}
                            </div>
                          )}
                        </div>
                        <div>
                          <span className={`status ${route.status === 'completada' ? 'paid' : route.status === 'en_curso' ? 'warning' : 'pending'}`}>
                            {route.status === 'completada' ? '✅ Completada' : route.status === 'en_curso' ? '🚚 En Curso' : '📋 Pendiente'}
                          </span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '20px', marginBottom: '15px', fontSize: '14px' }}>
                        <div>
                          <strong>Total Entregas:</strong> {route.total_entregas}
                        </div>
                        <div>
                          <strong>Completadas:</strong> <span style={{ color: route.entregas_completadas === route.total_entregas ? '#10b981' : '#f59e0b' }}>{route.entregas_completadas}</span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          onClick={() => openAssignModal(route)}
                          disabled={route.status === 'completada'}
                          style={{ padding: '8px 16px', background: route.status === 'completada' ? '#e2e8f0' : '#3b82f6', color: route.status === 'completada' ? '#94a3b8' : '#fff', border: 'none', borderRadius: '6px', cursor: route.status === 'completada' ? 'not-allowed' : 'pointer' }}
                        >
                          ➕ Asignar Entregas
                        </button>
                        {route.status === 'pendiente' && (
                          <button
                            onClick={() => handleUpdateRouteStatus(route.id, 'en_curso')}
                            style={{ padding: '8px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                          >
                            ▶️ Iniciar Ruta
                          </button>
                        )}
                        {route.status === 'en_curso' && (
                          <button
                            onClick={() => handleUpdateRouteStatus(route.id, 'completada')}
                            style={{ padding: '8px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                          >
                            ✅ Completar Ruta
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {routes.length === 0 && (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#64748b', background: '#fff', borderRadius: '12px' }}>
                      No hay rutas creadas. Crea una nueva ruta para comenzar.
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* TAB: RESUMEN DIARIO */}
          {activeTab === 'daily-summary' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3>📊 Resumen Consolidado Diario</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '600', color: '#475569' }}>Fecha:</label>
                  <input 
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                  />
                  <button
                    onClick={fetchDailySummary}
                    style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                  >
                    🔄 Actualizar
                  </button>
                </div>
              </div>
              
              {loading ? (
                <p className="loading">Cargando resumen...</p>
              ) : (
                <div className="table-wrapper">
                  <div className="table-responsive">
                    <table className="users-table">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Organismo de Tránsito</th>
                          <th>Total Entregas</th>
                          <th>Pendientes</th>
                          <th>En Ruta</th>
                          <th>Entregadas</th>
                          <th>Confirmadas</th>
                          <th>Incidencias</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dailySummary.map((item, idx) => (
                          <tr key={idx}>
                            <td><strong>{new Date(item.fecha).toLocaleDateString()}</strong></td>
                            <td>{item.transito_name}</td>
                            <td><strong>{item.total}</strong></td>
                            <td>{item.pendiente_recolectar}</td>
                            <td>{item.recolectado}</td>
                            <td>{item.entregado}</td>
                            <td style={{ color: '#10b981', fontWeight: 'bold' }}>{item.confirmado}</td>
                            <td style={{ color: '#ef4444', fontWeight: 'bold' }}>{item.incidencia}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {dailySummary.length === 0 && (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                      No hay datos de resumen diario.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* MODALES */}
      
      {/* Modal de Evidencia */}
      {showEvidenceModal && (
        <div className="modal-overlay" onClick={() => setShowEvidenceModal(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0 }}>📷 Registrar Evidencia - {evidenceType === 'placa' ? 'Placa' : 'Tarjeta Propiedad'}</h3>
              <button 
                onClick={() => setShowEvidenceModal(false)}
                style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleUploadEvidence}>
              <div className="modal-body" style={{ padding: '20px 0' }}>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '15px' }}>
                  Capture o seleccione una fotografía de {evidenceType === 'placa' ? 'la placa entregada' : 'la tarjeta de propiedad'}.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '140px', border: '2px dashed #cbd5e1', borderRadius: '10px', cursor: 'pointer', background: '#f8fafc' }}>
                    <span style={{ fontSize: '32px' }}>📷</span>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#475569', marginTop: '8px' }}>Tomar o Seleccionar Foto</span>
                    <input type="file" accept="image/*" capture="environment" onChange={handleFileChange} style={{ display: 'none' }} required />
                  </label>
                  {evidencePreview && (
                    <img src={evidencePreview} alt="Vista previa" style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
                  )}
                </div>
              </div>
              <div className="modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '15px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" onClick={() => setShowEvidenceModal(false)} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  disabled={uploading || !evidenceFile}
                  style={{ padding: '8px 16px', borderRadius: '6px', background: uploading || !evidenceFile ? '#cbd5e1' : '#2563eb', color: '#fff', border: 'none', cursor: uploading || !evidenceFile ? 'not-allowed' : 'pointer' }}
                >
                  {uploading ? '⏳ Registrando...' : 'Confirmar Entrega'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Incidencia */}
      {showIncidenceModal && (
        <div className="modal-overlay" onClick={() => setShowIncidenceModal(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0, color: '#dc2626' }}>⚠️ Reportar Incidencia</h3>
              <button 
                onClick={() => setShowIncidenceModal(false)}
                style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleReportIncidence}>
              <div className="modal-body" style={{ padding: '20px 0' }}>
                <textarea 
                  rows="4" 
                  placeholder="Detalle de la incidencia..."
                  value={incidenceReason}
                  onChange={(e) => setIncidenceReason(e.target.value)}
                  required
                  style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }}
                />
              </div>
              <div className="modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '15px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" onClick={() => setShowIncidenceModal(false)} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  disabled={submittingIncidence || !incidenceReason.trim()}
                  style={{ padding: '8px 16px', borderRadius: '6px', background: '#ef4444', color: '#fff', border: 'none', cursor: submittingIncidence || !incidenceReason.trim() ? 'not-allowed' : 'pointer' }}
                >
                  {submittingIncidence ? '⏳ Enviando...' : 'Reportar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Actualizar Contacto */}
      {showContactModal && (
        <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0 }}>📍 Actualizar Datos de Contacto</h3>
              <button 
                onClick={() => setShowContactModal(false)}
                style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleUpdateContact}>
              <div className="modal-body" style={{ padding: '20px 0' }}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Dirección de Entrega</label>
                  <input 
                    type="text"
                    value={contactData.direccion}
                    onChange={(e) => setContactData({...contactData, direccion: e.target.value})}
                    style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                    placeholder="Dirección completa..."
                  />
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Persona de Contacto</label>
                  <input 
                    type="text"
                    value={contactData.contacto}
                    onChange={(e) => setContactData({...contactData, contacto: e.target.value})}
                    style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                    placeholder="Nombre del contacto..."
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Teléfono</label>
                  <input 
                    type="text"
                    value={contactData.telefono}
                    onChange={(e) => setContactData({...contactData, telefono: e.target.value})}
                    style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                    placeholder="Número de teléfono..."
                  />
                </div>
              </div>
              <div className="modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '15px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" onClick={() => setShowContactModal(false)} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  style={{ padding: '8px 16px', borderRadius: '6px', background: '#2563eb', color: '#fff', border: 'none', cursor: 'pointer' }}
                >
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Crear Ruta */}
      {showRouteModal && (
        <div className="modal-overlay" onClick={() => setShowRouteModal(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0 }}>🗺️ Crear Nueva Ruta</h3>
              <button 
                onClick={() => setShowRouteModal(false)}
                style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleCreateRoute}>
              <div className="modal-body" style={{ padding: '20px 0' }}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Fecha de Ruta *</label>
                  <input 
                    type="date"
                    value={newRouteData.fecha_ruta}
                    onChange={(e) => setNewRouteData({...newRouteData, fecha_ruta: e.target.value})}
                    required
                    style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                  />
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Observaciones</label>
                  <textarea 
                    rows="3"
                    value={newRouteData.observaciones}
                    onChange={(e) => setNewRouteData({...newRouteData, observaciones: e.target.value})}
                    style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', resize: 'vertical' }}
                    placeholder="Detalles adicionales sobre la ruta..."
                  />
                </div>
              </div>
              <div className="modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '15px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" onClick={() => setShowRouteModal(false)} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  style={{ padding: '8px 16px', borderRadius: '6px', background: '#2563eb', color: '#fff', border: 'none', cursor: 'pointer' }}
                >
                  Crear Ruta
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Asignar Entregas a Ruta */}
      {showAssignModal && selectedRoute && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '80vh', overflow: 'auto' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '15px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0 }}>➕ Asignar Entregas a Ruta #{selectedRoute.id}</h3>
              <button 
                onClick={() => setShowAssignModal(false)}
                style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px 0' }}>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '15px' }}>
                Seleccione las entregas pendientes de recolección para asignar a esta ruta:
              </p>
              <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                {deliveries.filter(d => d.status === 'pendiente_recolectar' && !d.route_id).map(d => (
                  <label key={d.id} style={{ display: 'flex', alignItems: 'center', padding: '12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: selectedDeliveries.includes(d.id) ? '#eff6ff' : '#fff' }}>
                    <input 
                      type="checkbox"
                      checked={selectedDeliveries.includes(d.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDeliveries([...selectedDeliveries, d.id]);
                        } else {
                          setSelectedDeliveries(selectedDeliveries.filter(id => id !== d.id));
                        }
                      }}
                      style={{ marginRight: '12px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>{d.plate_number} - #{d.tramite_id}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{d.concesionario_name} | {d.transito_name}</div>
                    </div>
                  </label>
                ))}
              </div>
              {deliveries.filter(d => d.status === 'pendiente_recolectar' && !d.route_id).length === 0 && (
                <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                  No hay entregas pendientes disponibles para asignar.
                </div>
              )}
            </div>
            <div className="modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '15px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button type="button" onClick={() => setShowAssignModal(false)} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>
                Cancelar
              </button>
              <button 
                onClick={handleAssignDeliveries}
                disabled={selectedDeliveries.length === 0}
                style={{ padding: '8px 16px', borderRadius: '6px', background: selectedDeliveries.length === 0 ? '#cbd5e1' : '#2563eb', color: '#fff', border: 'none', cursor: selectedDeliveries.length === 0 ? 'not-allowed' : 'pointer' }}
              >
                Asignar {selectedDeliveries.length} Entrega{selectedDeliveries.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlateDeliveries;
